// Caché compartida para el listado "Todos los contactos" (unión de todas las
// agendas del usuario). Evita re-lanzar N `getDocs` cada vez que se abre la
// vista "all", se exporta el CSV consolidado, o se copian todos los emails.
//
// Estrategia:
//  - Caché en memoria (rápida, dentro de la misma sesión React).
//  - Respaldo en sessionStorage para sobrevivir a recargas de la misma pestaña.
//  - Parcheo local cuando el propio tab muta un contacto: evita refetch en la
//    misma pestaña.
//  - Sincronización multi-pestaña vía BroadcastChannel: cuando otro tab muta,
//    invalidamos nuestro caché para refetchear datos frescos en el próximo uso.
//  - Invalidación explícita desde cada mutación que no podemos expresar como
//    patch (casos ambiguos).
//
// No usamos localStorage a propósito: los contactos pueden ser muchos y no
// queremos dejarlos persistidos entre sesiones.

const TTL_MS = 60 * 60 * 1000; // 1 hora
const STORAGE_KEY = 'privileads:allContactsCache:v1';
const CHANNEL_NAME = 'privileads:contactsCache';

const memoryCache = { key: null, data: null, timestamp: 0 };

// ---- BroadcastChannel para sincronizar entre pestañas ----
let channel = null;
try {
    if (typeof BroadcastChannel !== 'undefined') {
        channel = new BroadcastChannel(CHANNEL_NAME);
        channel.addEventListener('message', (e) => {
            const msg = e && e.data;
            if (!msg || msg.type !== 'invalidate') return;
            // Otra pestaña mutó datos: limpiamos nuestro caché para que el
            // próximo acceso traiga lo actualizado desde Firestore una sola vez.
            memoryCache.key = null;
            memoryCache.data = null;
            memoryCache.timestamp = 0;
            clearSessionStorage();
        });
    }
} catch (_) {
    channel = null;
}

function broadcastInvalidate() {
    try {
        channel?.postMessage({ type: 'invalidate' });
    } catch (_) {
        /* noop */
    }
}

// ---- Helpers internos ----
function buildCacheKey(user, agendas) {
    if (!user) return null;
    const agendaIds = (agendas || []).map((a) => a.id).sort().join(',');
    return `${user.uid}-${agendaIds}`;
}

function readFromSessionStorage() {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        if (!parsed.key || !Array.isArray(parsed.data) || !parsed.timestamp) return null;
        return parsed;
    } catch (_) {
        return null;
    }
}

function writeToSessionStorage(entry) {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
    } catch (_) {
        // Cuota excedida u otro error → lo ignoramos, el caché en memoria sigue operativo.
    }
}

function clearSessionStorage() {
    try {
        sessionStorage.removeItem(STORAGE_KEY);
    } catch (_) {
        /* noop */
    }
}

function matchesId(item, idSet) {
    return idSet.has(item.id) || idSet.has(item.placeId);
}

// Aplica `updater` al contenido cacheado (solo si está cargado para el mismo
// user+agendas) y sincroniza memoria + sessionStorage + pestañas.
function applyLocalMutation(user, agendas, updater) {
    const key = buildCacheKey(user, agendas);
    if (!key) return;

    if (memoryCache.key === key && memoryCache.data) {
        const next = updater(memoryCache.data);
        if (Array.isArray(next)) {
            memoryCache.data = next;
            memoryCache.timestamp = Date.now();
            writeToSessionStorage({
                key,
                data: next,
                timestamp: memoryCache.timestamp,
            });
        }
    }
    // Otras pestañas, aun si tienen datos cacheados (copias independientes en
    // sessionStorage), necesitan refrescar. Les enviamos una señal para que
    // limpien el suyo y refetcheen al próximo uso.
    broadcastInvalidate();
}

// ---- API pública ----
export function getCachedAllContacts(user, agendas) {
    const key = buildCacheKey(user, agendas);
    if (!key) return null;
    const now = Date.now();

    if (
        memoryCache.key === key &&
        memoryCache.data &&
        now - memoryCache.timestamp < TTL_MS
    ) {
        return memoryCache.data;
    }

    const stored = readFromSessionStorage();
    if (stored && stored.key === key && now - stored.timestamp < TTL_MS) {
        memoryCache.key = stored.key;
        memoryCache.data = stored.data;
        memoryCache.timestamp = stored.timestamp;
        return stored.data;
    }

    return null;
}

// Setter "silencioso": se usa cuando sembramos el caché desde un fetch recién
// hecho. NO dispara broadcast, porque no representa una mutación.
export function setCachedAllContacts(user, agendas, data) {
    const key = buildCacheKey(user, agendas);
    if (!key || !Array.isArray(data)) return;
    const entry = { key, data, timestamp: Date.now() };
    memoryCache.key = entry.key;
    memoryCache.data = entry.data;
    memoryCache.timestamp = entry.timestamp;
    writeToSessionStorage(entry);
}

// Aplica el mismo `partial` a todos los contactos cuyo `id` o `placeId` esté
// en `ids`. Si el caché local no tiene una entrada para este usuario/agendas,
// no hace trabajo local pero igual avisa a otras pestañas.
export function patchCachedContacts(user, agendas, ids, partial) {
    if (!ids || ids.length === 0 || !partial) {
        // Nada que patchear, pero sí podemos seguir informando a otras pestañas
        // si la intención fue una mutación (aunque no haya cambios).
        return;
    }
    const idSet = new Set(ids);
    applyLocalMutation(user, agendas, (items) =>
        items.map((item) => (matchesId(item, idSet) ? { ...item, ...partial } : item))
    );
}

// Versión flexible: un updater genérico aplicado al array cacheado. Útil
// cuando los cambios por contacto son distintos (ej. notas concatenadas).
export function updateCachedContacts(user, agendas, updater) {
    if (typeof updater !== 'function') return;
    applyLocalMutation(user, agendas, updater);
}

// Elimina del caché los contactos cuyo `id` o `placeId` esté en `ids`.
export function removeCachedContacts(user, agendas, ids) {
    if (!ids || ids.length === 0) return;
    const idSet = new Set(ids);
    applyLocalMutation(user, agendas, (items) =>
        items.filter((item) => !matchesId(item, idSet))
    );
}

// Invalidación total (útil para operaciones ambiguas o cuando no sabemos
// exactamente qué cambió).
export function invalidateContactsCache() {
    memoryCache.key = null;
    memoryCache.data = null;
    memoryCache.timestamp = 0;
    clearSessionStorage();
    broadcastInvalidate();
}
